// Selection — a value object representing a (start, end) character range
// inside the editor's content, plus a tracker that mirrors the live DOM
// selection into the model.
//
// The original implementation walked every text node in the editor on
// every selection read. With the line-block diff render in document.js
// each line lives inside its own <span data-block> wrapper, so we can
// binary-search the blocks array to find the right one and walk only
// its text nodes — O(log B + line length) instead of O(N).

function dispatch(target, name, detail = null, cancelable = false) {
  return target.dispatchEvent(new CustomEvent(name, { bubbles: true, detail, cancelable }))
}

export class Selection {
  start = 0
  end = 0

  constructor(container, start, end) {
    this.container = container
    this.start = start
    this.end = end
  }

  static fromDOMRange(domRange, container, blocks) {
    const start = offsetForPoint(container, blocks, domRange.startContainer, domRange.startOffset)
    if (domRange.collapsed) return new Selection(container, start, start)
    const end = offsetForPoint(container, blocks, domRange.endContainer, domRange.endOffset)
    return new Selection(container, start, end)
  }

  toDOMRange(blocks) {
    const range = document.createRange()
    const [startNode, startOffset] = locateOffset(this.container, blocks, this.start)
    const [endNode, endOffset] = locateOffset(this.container, blocks, this.end)
    range.setStart(startNode, startOffset)
    range.setEnd(endNode, endOffset)
    return range
  }

  isEqual(other) {
    return this.container === other.container && this.start === other.start && this.end === other.end
  }
}

// Resolve a (DOM node, offset) pair to a character offset in the model.
function offsetForPoint(container, blocks, node, offset) {
  // Find the data-block wrapper that contains this point.
  const blockEl = ancestorBlock(node, container)
  if (!blockEl) {
    // Selection landed outside any block — fall back to total length.
    return modelLength(blocks)
  }

  const blockIndex = indexOfBlockEl(container, blockEl)
  if (blockIndex < 0 || blockIndex >= blocks.length) return modelLength(blocks)

  const blockStart = blocks[blockIndex].start
  const intra = offsetWithinBlock(blockEl, node, offset)
  return blockStart + intra
}

function ancestorBlock(node, container) {
  let current = node
  if (current && current.nodeType === Node.TEXT_NODE) current = current.parentElement
  while (current && current !== container) {
    if (current.nodeType === Node.ELEMENT_NODE && current.hasAttribute("data-block")) return current
    current = current.parentElement
  }
  return null
}

function indexOfBlockEl(container, blockEl) {
  const children = container.children
  for (let i = 0; i < children.length; i++) {
    if (children[i] === blockEl) return i
  }
  return -1
}

function offsetWithinBlock(blockEl, node, offset) {
  if (node === blockEl) {
    // Offset references a child index — count text length up to that child.
    let total = 0
    for (let i = 0; i < offset && i < blockEl.childNodes.length; i++) {
      total += textLengthOf(blockEl.childNodes[i])
    }
    return total
  }

  let total = 0
  const walker = document.createTreeWalker(blockEl, NodeFilter.SHOW_TEXT)
  while (walker.nextNode()) {
    if (walker.currentNode === node) return total + offset
    total += walker.currentNode.textContent.length
  }
  return total
}

function textLengthOf(node) {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent.length
  return node.textContent ? node.textContent.length : 0
}

function locateOffset(container, blocks, target) {
  if (blocks.length === 0) return [container, 0]

  const totalLength = modelLength(blocks)
  if (target >= totalLength) {
    // Place at end of last block's last text node, or after the wbr.
    const last = blocks[blocks.length - 1]
    if (!last.lineEl) return [container, container.childNodes.length]
    return tailOfBlock(last.lineEl)
  }

  let lo = 0
  let hi = blocks.length - 1
  while (lo < hi) {
    const mid = (lo + hi) >>> 1
    if (blocks[mid].end <= target) lo = mid + 1
    else hi = mid
  }
  const block = blocks[lo]
  if (!block.lineEl) return [container, 0]

  const intra = target - block.start
  return walkInto(block.lineEl, intra)
}

function walkInto(blockEl, intra) {
  const walker = document.createTreeWalker(blockEl, NodeFilter.SHOW_TEXT)
  let consumed = 0
  while (walker.nextNode()) {
    const len = walker.currentNode.textContent.length
    if (consumed + len >= intra) {
      return [walker.currentNode, intra - consumed]
    }
    consumed += len
  }
  return tailOfBlock(blockEl)
}

function tailOfBlock(blockEl) {
  // Walk to the last text node, place at its end.
  const walker = document.createTreeWalker(blockEl, NodeFilter.SHOW_TEXT)
  let last = null
  while (walker.nextNode()) last = walker.currentNode
  if (last) return [last, last.textContent.length]
  return [blockEl, blockEl.childNodes.length]
}

function modelLength(blocks) {
  if (blocks.length === 0) return 0
  return blocks[blocks.length - 1].end
}

// SelectionTracker — bound to a single Document, mirrors window.getSelection()
// into model offsets and dispatches house-md:selectionchange.
export class SelectionTracker {
  constructor(document) {
    this.document = document
    this.range = new Selection(this.contentElement, 0, 0)
    this.selectionText = ""
  }

  current() {
    return { start: this.start, end: this.end, selection: this.selectionText }
  }

  currentLine() {
    return this.lineAt(this.end)
  }

  previousLine() {
    const { content } = this.document
    const lineStart = Math.max(content.lastIndexOf("\n", this.start - 1), 0)
    return this.lineAt(lineStart)
  }

  lineAt(position) {
    const { content } = this.document
    const start = Math.max(content.lastIndexOf("\n", position - 1) + 1, 0)
    const newlineIdx = content.indexOf("\n", position)
    const end = newlineIdx === -1 ? content.length : newlineIdx + 1
    return { start, end, content: content.slice(start, end) }
  }

  select({ start, end }) {
    this.range = new Selection(this.contentElement, start, end)
    const domRange = this.range.toDOMRange(this.document.blocks)
    if (!domRange) {
      console.error("Failed to create range", { start, end })
      return
    }
    const sel = window.getSelection()
    sel.removeAllRanges()
    sel.addRange(domRange)
    this.update()
  }

  update = () => {
    const sel = document.getSelection()
    if (!sel || sel.rangeCount === 0) return
    const liveRange = sel.getRangeAt(0)
    const previous = this.range
    if (liveRange.intersectsNode(this.contentElement)) {
      this.range = Selection.fromDOMRange(liveRange, this.contentElement, this.document.blocks)
      this.selectionText = this.document.content.slice(this.start, this.end)
    }
    if (!this.range.isEqual(previous)) {
      dispatch(this.element, "house-md:selectionchange", { start: this.start, end: this.end })
    }
  }

  get start() {
    return this.range.start
  }

  get end() {
    return this.range.end
  }

  get element() {
    return this.document.element
  }

  get contentElement() {
    return this.document.contentElement
  }

  get isEmpty() {
    return this.start === this.end
  }

  get isMultiline() {
    return this.current().selection.includes("\n")
  }

  get isBold() {
    return this.matchesAncestor("strong")
  }

  get isItalic() {
    return this.matchesAncestor("em")
  }

  get isStrikethrough() {
    return this.matchesAncestor("s")
  }

  get isLink() {
    return this.matchesAncestor(".link")
  }

  get isCode() {
    return this.matchesAncestor(".code")
  }

  get isQuote() {
    return this.matchesAncestor(".quote")
  }

  get isBulletList() {
    return this.matchesAncestor(".ul-li")
  }

  get isNumberList() {
    return this.matchesAncestor(".ol-li")
  }

  matchesAncestor(selector) {
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0) return false
    let target = sel.getRangeAt(0).endContainer
    if (target.nodeType === Node.TEXT_NODE) target = target.parentElement
    if (!target || !this.contentElement.contains(target)) return false
    return !!target.closest(selector)
  }
}
